import { PureDataMarkerProps } from '@weenat/client/dist/core/map'
import { fromColorPathToColor, WeenatColor } from '@weenat/theme'
import Icons from 'app/src/kit/Icons'
import HydricStressTrendIcon from 'app/src/kit/icons/HydricStressTrendIcon'
import LastUpdate from 'app/src/kit/LastUpdate'
import { BoxProps } from 'app/src/kit/primitives/themeMappings/props'
import { isNil } from 'lodash-es'
import { FC, memo, RefCallback, useCallback, useState } from 'react'
import { styled } from 'styled-components'
import MarkerAnchor from './MarkerAnchor'
import StatusIndicator from './StatusIndicator'

const BORDER_WIDTH = 2

// .attrs() injects --pill-scale as an inline CSS custom property so styled-components
// never generates a new class per unique scale value across hundreds of markers.
const PillContainer = styled(Flex).attrs<{ $isFocused?: boolean }>(({ $isFocused }) => ({
  style: { '--pill-scale': $isFocused ? 1.15 : 1 } as React.CSSProperties
}))`
  transform-origin: bottom center;
  transform: scale(var(--pill-scale));
  transition: transform 150ms ease-out;
`

// .attrs() resolves the WeenatColor path to a hex and writes it as --delay-indicator-border-color.
// Avoids a new CSS class whenever borderColor changes (on focus/hover state transitions).
const DelayIndicator = styled(Flex).attrs(({ $borderColor }) => ({
  style: {
    '--delay-indicator-border-color':
      $borderColor != null ? fromColorPathToColor($borderColor) : undefined
  } as React.CSSProperties
}))`
  position: absolute;

  bottom: 38px;
  right: -${BORDER_WIDTH}px;

  background-color: ${(p) => p.theme.colors.grayscale.white};

  border: ${BORDER_WIDTH}px solid var(--delay-indicator-border-color);
  border-radius: ${(p) => p.theme.radiuses.md}px ${(p) => p.theme.radiuses.md}px 0px 0px;

  padding: ${(p) => p.theme.spacings.sm}px;

  white-space: nowrap;
`

interface DataMarkerProps extends PureDataMarkerProps {
  /** @default 'sm' */
  boxShadow?: BoxProps['$boxShadow']
  timezone: string | undefined
}

const PureDataMarker: FC<DataMarkerProps> = ({
  backgroundColor,
  boxShadow = 'sm',
  color,
  content,
  dangerThreshold,
  hideAnchor = false,
  iconSize = 'lg',
  isFocused = false,
  lastUpdate,
  opacity,
  showContent = true,
  showLastUpdate,
  showStatusIndicator,
  status,
  timezone,
  trend,
  type,
  warningThreshold
}) => {
  const [delayIndicatorWidth, setDelayIndicatorWidth] = useState<number | undefined>(undefined)

  const getDelayIndictorWidth: RefCallback<HTMLDivElement> = useCallback((ref) => {
    setDelayIndicatorWidth(ref?.clientWidth)
  }, [])

  const Icon =
    type === 'virtual_device'
      ? Icons.SingleVirtualSensorFilled
      : type === 'device' || type === 'station'
        ? Icons.SingleSensorFilled
        : Icons.PlotsFilled

  const containerBorder: WeenatColor = isFocused ? 'primary.500' : color
  const markerBorder: WeenatColor = isFocused ? 'primary.500' : 'grayscale.black'

  return (
    <Flex $flexDirection='column' $justifyContent='center' $alignItems='center' $gap={'sm'}>
      <PillContainer
        $isFocused={isFocused}
        $position='relative'
        $alignItems='center'
        $backgroundColor={backgroundColor}
        $borderRadius={'md'}
        $borderColor={containerBorder}
        $borderTopRightRadius={showLastUpdate ? 0 : 'md'}
        $borderWidth={BORDER_WIDTH}
        $minWidth={!isNil(delayIndicatorWidth) ? delayIndicatorWidth + 24 : undefined}
        $p={'md'}
        $gap={'md'}
        $boxShadow={boxShadow}
        $opacity={opacity}
      >
        <Icon $size={iconSize} $color={color} />
        {showContent ? content : null}
        <HydricStressTrendIcon trend={trend} $color={color} $size='md' />

        {showLastUpdate ? (
          <DelayIndicator $borderColor={containerBorder} ref={getDelayIndictorWidth}>
            <LastUpdate
              timestamp={lastUpdate}
              dangerThreshold={dangerThreshold}
              warningThreshold={warningThreshold}
              format='narrow'
              iconSize='sm'
              fontSize='xs'
              timezone={timezone}
              disableTooltip
            />
          </DelayIndicator>
        ) : null}
        {showStatusIndicator ? <StatusIndicator status={status} /> : null}
      </PillContainer>
      {hideAnchor ? null : <MarkerAnchor $borderColor={markerBorder} />}
    </Flex>
  )
}

export default memo(PureDataMarker)
